/**
 * The five questions on the home page (navigation re-cut, option A).
 *
 * Each card is a question a person arrives with, answered by one figure
 * from the one posture (stitching review 5.4) and the pages that say
 * more. The figure is the posture's, so it reads the same here as on the
 * page it opens. A question with nothing this seat may open is left out,
 * as it is from the masthead.
 */
import { Explainer } from "shared/components/Explainer";
import type { TFunction } from "i18next";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import type { Posture } from "shared/api/types";
import { useMe } from "shared/api/useMe";
import { useLicenseStatus } from "shared/licensing/useLicense";
import {
  QUESTIONS,
  type QuestionKey,
  visibleDestinations,
} from "shared/layout/questions";
import { usePosture } from "shared/posture/usePosture";
import { useMyWork } from "shared/work/useMyWork";
import { listOf } from "shared/i18n/listOf";

function answer(
  key: QuestionKey,
  p: Posture | undefined,
  needsYou: number | undefined,
  t: TFunction,
): string | null {
  const base = "home.questions.answer";
  if (key === "do") {
    if (needsYou === undefined) return null;
    return needsYou === 0 ? t(`${base}.doNone`) : t(`${base}.do`, { count: needsYou });
  }
  if (!p || !p.register_services) return null;
  switch (key) {
    case "matters": {
      // Round 18 P9 -- one register count: with nothing admitted and
      // something under assessment, say what is under assessment, as the
      // portfolio does, services and functions apart.
      const underServices = p.under_assessment_services?.count ?? 0;
      const underFunctions = p.under_assessment_functions?.count ?? 0;
      if (
        p.register_services.count === 0 &&
        (p.register_functions?.count ?? 0) === 0 &&
        underServices + underFunctions > 0
      ) {
        return t(`${base}.mattersNothingAdmitted`, {
          under: listOf(
            [
              underServices > 0 &&
                t(`${base}.mattersServices`, { count: underServices }),
              underFunctions > 0 &&
                t(`${base}.mattersFunctions`, { count: underFunctions }),
            ].filter((x): x is string => typeof x === "string"),
          ),
          mvb: p.mvb_impacted?.of ?? 0,
        });
      }
      return t(`${base}.matters`, {
        services: t(`${base}.mattersServices`, { count: p.register_services.count }),
        functions: t(`${base}.mattersFunctions`, {
          count: p.register_functions?.count ?? 0,
        }),
        mvb: p.mvb_impacted?.of ?? 0,
      });
    }
    case "safe":
      return t(`${base}.safe`, {
        count: p.impacted_services?.count ?? 0,
        of: p.impacted_services?.of ?? 0,
      });
    case "owe":
      return t(`${base}.owe`, {
        governed: p.governed?.count ?? 0,
        of: p.governed?.of ?? 0,
        tested: p.covered?.cells_tested ?? 0,
        cells: p.covered?.cells_total ?? 0,
        regime: (p.covered?.regime_code ?? "").toUpperCase(),
      });
    case "prove":
      return p.score?.not_scored_reason
        ? t(`${base}.proveNotScored`)
        : p.score?.score == null
          ? t(`${base}.proveNone`)
          : t(`${base}.prove`, { tier: p.score.tier, score: p.score.score.toFixed(1) });
  }
}

export function FiveQuestions() {
  const { t } = useTranslation();
  const me = useMe();
  const lic = useLicenseStatus();
  const posture = usePosture();
  const work = useMyWork();
  return (
    <section
      data-testid="five-questions"
      aria-label={t("home.questions.heading")}
      className="mb-6 grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]"
    >
      {QUESTIONS.map((q, i) => {
        const dests = visibleDestinations(q, me.data, lic.data);
        if (dests.length === 0) return null;
        const line =
          q.key === "do" && work.data?.reads_only
            ? t("home.questions.answer.doReadsOnly")
            : answer(q.key, posture.data, work.data?.needs_you, t);
        return (
          <article
            key={q.key}
            data-testid="question-card"
            data-question={q.key}
            className="flex flex-col gap-3 rounded-xl border border-paper-2 bg-white p-5 shadow-card"
          >
            <span className="font-mono text-[11px] text-brand-brassDark">{i + 1}</span>
            <h2 className="font-display text-xl leading-tight text-ink">
              <Link to={dests[0]!.to} className="hover:text-brand-brassDark">
                {t(q.questionKey)}
              </Link>
            </h2>
            <p data-testid="question-card-answer" className="text-sm text-ink-2">
              {line ?? " "}
            </p>
            {/* Round 17 G21 -- MVB is first met here ("3 minimum-viable")
                and was taught nowhere on the page; it is taught once. */}
            {q.key === "matters" && <Explainer termKey="mvb" />}
            <div className="mt-auto flex flex-wrap gap-x-3.5 gap-y-1 text-[13px]">
              {dests.slice(0, 3).map((d) => (
                <Link
                  key={d.to}
                  to={d.to}
                  className="font-medium text-brand-brassDark hover:underline"
                >
                  {t(d.labelKey)}
                </Link>
              ))}
            </div>
          </article>
        );
      })}
    </section>
  );
}
